/**
 * UNA VENTANA A LA VEZ.
 *
 * Encadenar hojas es normal en esta app: «Añadir producto» abre «Escanear
 * factura», y el escaneo abre el aviso de factura duplicada. Lo que no es
 * normal es VERLAS TODAS: la de detrás asoma por los bordes, cada una trae su
 * propio velo (así que la pantalla se oscurece el doble) y el conjunto parece
 * un montón de papeles, no una pantalla.
 *
 * La solución que ya había en el código era esconder la de detrás quitándola
 * del árbol (`open={showDuplicateDialog && !confirmCreateNewOpen}`), y eso
 * FUNCIONA pero pierde su estado: Radix desmonta el contenido al cerrar. Aquí
 * la de detrás se queda montada y viva, solo deja de verse (`visibility`, no
 * `display`: el alto de la hoja se sigue midiendo igual). Al cerrar la de
 * encima reaparece exactamente como estaba, que es lo que espera quien pulsa la
 * flecha de volver.
 *
 * Dos piezas:
 *
 *   - `useDialogVeilHost()` — lo usa la carcasa que PUEDE taparse (FormSheet
 *     con `veilable`). Devuelve `veiled` y el `host` que se reparte por
 *     contexto a lo que lleve dentro.
 *   - `useVeilAncestor(active)` — lo llama quien abre algo ENCIMA. Mientras
 *     `active` sea cierto, la carcasa de arriba se aparta.
 *
 * Es opt-in por los dos lados a propósito: son cuarenta y cuatro pantallas las
 * que montan `FormSheet`, y taparlas todas por defecto cambiaría sitios que hoy
 * se apoyan en ver la de detrás.
 */
import * as React from "react";

interface DialogVeilHost {
  /** Tapa la carcasa mientras no se llame a la función devuelta. */
  acquire: () => () => void;
}

const DialogVeilContext = React.createContext<DialogVeilHost | null>(null);

export const DialogVeilProvider = DialogVeilContext.Provider;

/**
 * Tapar la carcasa de arriba mientras `active`.
 *
 * Sin carcasa que tape (el escáner montado suelto en una página, no dentro de
 * una hoja) no hace nada: el contexto viene vacío y esto es un no-op.
 */
export function useVeilAncestor(active: boolean): void {
  const host = React.useContext(DialogVeilContext);
  React.useEffect(() => {
    if (!active || !host) return;
    return host.acquire();
  }, [active, host]);
}

/** El otro lado: la carcasa que se deja tapar. */
export function useDialogVeilHost(): { veiled: boolean; host: DialogVeilHost } {
  const [count, setCount] = React.useState(0);

  const acquire = React.useCallback(() => {
    setCount((c) => c + 1);
    /* Soltar DOS veces no puede restar dos: React llama la limpieza de un
       efecto una sola vez, pero en modo estricto los efectos se montan y
       desmontan por parejas, y un contador que baja de más deja la hoja tapada
       para siempre. */
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setCount((c) => Math.max(0, c - 1));
    };
  }, []);

  const host = React.useMemo(() => ({ acquire }), [acquire]);
  return { veiled: count > 0, host };
}

/**
 * Lo que se le pone a la hoja tapada.
 *
 * `invisible` y no `hidden`: con `display:none` el navegador deja de calcular
 * su caja, y esta app MIDE las hojas para decidir su alto (`useSheetFit`) — al
 * volver reaparecería con el alto en cero. `visibility:hidden` la saca de la
 * vista y del árbol de accesibilidad sin tocar la medida.
 */
export const VEILED_CLASS = "invisible pointer-events-none";
